import {
  ClipboardDocumentListIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline";
import {
  Columns4,
  AudioLines,
  Printer,
  Undo,
  Redo,
  Copy,
  Users,
  Grip,
  Tags,
  KeyboardIcon,
  SquareDashed,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import {
  useDrillViewData,
  useFieldEditorData,
  usePlaybackData,
  useCollaborationData,
} from "../hooks/useFieldEditorShared.tsx";

export default function FieldEditorMenuBar() {
  // Get data from context hooks
  const { drill, view } = useDrillViewData();
  const { fieldEditorData, setFieldEditorData } = useFieldEditorData();
  const {
    playbackState,
    setPlaybackState,
    handlePageChange,
    handleCountChange,
  } = usePlaybackData();
  const collaborationReturn = useCollaborationData();
  const [activeMenu, setActiveMenu] = useState("");

  const { drillModifiedByCollaboration, collaboration } = collaborationReturn;

  const [tempo, setTempo] = useState(
    drill?.pages[drill?.selectedPageIndex ?? 0]?.tempo ?? 120
  );
  const [counts, setCounts] = useState(
    drill?.selectedPageIndex === 0
      ? 0
      : drill?.pages[drill?.selectedPageIndex ?? 0]?.counts || 8
  );

  useEffect(() => {
    setTempo(drill?.pages[drill?.selectedPageIndex ?? 0]?.tempo ?? 120);
    setCounts(
      drill?.selectedPageIndex === 0
        ? 0
        : drill?.pages[drill?.selectedPageIndex ?? 0]?.counts || 8
    );
  }, [drill, drillModifiedByCollaboration, drill?.selectedPageIndex]);

  const toggleMenu = (menu: string) => {
    if (activeMenu === menu) {
      setActiveMenu("");
    } else {
      setActiveMenu(menu);
    }
  };

  const undoChange = useCallback(() => {
    if (collaboration) {
      collaboration.undo();
    }
  }, [collaboration]);

  const redoChange = useCallback(() => {
    if (collaboration) {
      collaboration.redo();
    }
  }, [collaboration]);
  const selectAllPerformers = useCallback(() => {
    if (view && view.getEngine()) {
      view.getEngine().selectAllPerformers();
    }
  }, [view]);

  return (
    <div>
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-screen-xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1">
              <div className="relative">
                <button
                  type="button"
                  data-tour="project-menu"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMenu("file");
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${activeMenu === "file" ? "bg-gray-100" : "hover:bg-gray-50"
                    }`}
                >
                  Project
                </button>
                {activeMenu === "file" && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-md shadow-lg border py-1 z-50 project-dropdown">
                    <button
                      className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100"
                      onClick={() => {
                        setFieldEditorData({
                          ...fieldEditorData,
                          showFeildSettingsModal:
                            !fieldEditorData.showFeildSettingsModal,
                        });
                        toggleMenu("file");
                      }}
                    >
                      <Columns4 className="mr-2 h-4 w-4" />
                      Field Settings
                      {/* <span className="ml-auto text-xs text-gray-500">
                          ⌘N
                        </span> */}
                    </button>
                    <button
                      type="button"
                      data-tour="audio"
                      className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100 audio-select"
                      onClick={() => {
                        setFieldEditorData({
                          ...fieldEditorData,
                          showAudioTrackModal:
                            !fieldEditorData.showAudioTrackModal,
                        });
                        toggleMenu("file");
                      }}
                    >
                      <AudioLines className="mr-2 h-4 w-4" />
                      Audio
                      {/* <span className="ml-auto text-xs text-gray-500">
                          ⌘O
                        </span> */}
                    </button>

                    <div className="h-px bg-gray-200 my-1" />
                    <button
                      type="button"
                      data-tour="export-print"
                      className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100"
                      onClick={() => {
                        setFieldEditorData({
                          ...fieldEditorData,
                          showExportDialog: !fieldEditorData.showExportDialog,
                        });
                        toggleMenu("file");
                      }}
                    >
                      <Printer className="mr-2 h-4 w-4" />
                      Print
                      {/* <span className="ml-auto text-xs text-gray-500">
                          ⌘O
                        </span> */}
                    </button>
                  </div>
                )}
              </div>
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMenu("edit");
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${activeMenu === "edit" ? "bg-gray-100" : "hover:bg-gray-50"
                    }`}
                >
                  Edit
                </button>
                {activeMenu === "edit" && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-md shadow-lg border py-1 z-50">
                    <button
                      className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100"
                      onClick={undoChange}
                    >
                      <Undo className="mr-2 h-4 w-4" />
                      Undo
                      <span className="ml-auto text-xs text-gray-500">⌘Z</span>
                    </button>
                    <button
                      className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100"
                      onClick={redoChange}
                    >
                      <Redo className="mr-2 h-4 w-4" />
                      Redo
                      <span className="ml-auto text-xs text-gray-500">⌘Y</span>
                    </button>
                    <button
                      className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100"
                      onClick={selectAllPerformers}
                    >
                      <SquareDashed className="mr-2 h-4 w-4" />
                      Select All Performers
                      {/* <span className="ml-auto text-xs text-gray-500">⌘A</span> */}
                    </button>
                    {/* <div className="h-px bg-gray-200 my-1" /> */}
                    {/* <button className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100">
                      <Copy className="mr-2 h-4 w-4" />
                      Copy
                      <span className="ml-auto text-xs text-gray-500">⌘C</span>
                    </button>
                    <button className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100">
                      <ClipboardDocumentListIcon className="mr-2 h-4 w-4" />
                      Paste
                      <span className="ml-auto text-xs text-gray-500">⌘V</span>
                    </button> */}
                  </div>
                )}
              </div>
              <div className="relative">
                <button
                  type="button"
                  data-tour="performers-menu"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMenu("performers");
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors performer-dropdown ${activeMenu === "performers"
                    ? "bg-gray-100"
                    : "hover:bg-gray-50"
                    }`}
                >
                  Performers
                </button>
                {activeMenu === "performers" && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-md shadow-lg border py-1 z-50">
                    <button
                      type="button"
                      data-tour="performer-modal-open"
                      className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100 performer-set-edit"
                      onClick={() => {
                        setFieldEditorData({
                          ...fieldEditorData,
                          showPerformerLabelModal:
                            !fieldEditorData.showPerformerLabelModal,
                        });
                        toggleMenu("performers");
                      }}
                    >
                      <Users className="mr-2 h-4 w-4" />
                      Performers
                    </button>
                    <button
                      className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100"
                      onClick={() => {
                        setFieldEditorData({
                          ...fieldEditorData,
                          showCoordinateSheetModal:
                            !fieldEditorData.showCoordinateSheetModal,
                        });
                        toggleMenu("performers");
                      }}
                    >
                      <Grip className="mr-2 h-4 w-4" />
                      Coordinate Sheets
                    </button>
                    <button
                      className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={
                        (drill &&
                          (drill?.engine?.selectedPerformers?.length ?? 0) <=
                          1) ??
                        true
                      }
                      onClick={() => {
                        setFieldEditorData({
                          ...fieldEditorData,
                          showBulkEditPerformerLabelModal:
                            !fieldEditorData.showBulkEditPerformerLabelModal,
                        });
                        toggleMenu("performers");
                      }}
                      title="Change Symbol - Select performers to change multiple symbols at once"
                    >
                      <Tags className="mr-2 h-4 w-4" />
                      Change Symbol
                      <span className="ml-4 text-[10px] bg-blue-500 text-white rounded-full px-1.5 py-0.5 leading-none shadow">
                        NEW
                      </span>
                    </button>
                  </div>
                )}
              </div>
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMenu("help");
                  }}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${activeMenu === "help" ? "bg-gray-100" : "hover:bg-gray-50"
                    }`}
                >
                  Help
                </button>
                {activeMenu === "help" && (
                  <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-md shadow-lg border py-1 z-50">
                    <a
                      className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100"
                      href="https://help.drillflo.com"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <QuestionMarkCircleIcon className="mr-2 h-4 w-4" />
                      Help Documents
                    </a>
                    <button
                      className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100"
                      onClick={() => {
                        setFieldEditorData({
                          ...fieldEditorData,
                          isShortcutsOpen: !fieldEditorData.isShortcutsOpen,
                        });
                        toggleMenu("help");
                      }}
                    >
                      <KeyboardIcon className="mr-2 h-4 w-4" />
                      Keyboard shortcuts
                    </button>
                    <div className="h-px bg-gray-200 my-1" />
                    {/* <button className="w-full px-4 py-1.5 text-sm flex items-center hover:bg-gray-100">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </button> */}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2 bg-gray-50 rounded-md border overflow-hidden">
              <button
                onClick={() => view?.updateDrillZoom(0.9)}
                className="p-2.5 rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
                title="Zoom Out"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
              </button>
              <span className="text-xs font-medium text-gray-500">
                {Number((view?.getCurrentZoom() ?? 1) * 100).toFixed(0)}%
              </span>
              <button
                onClick={() => view?.updateDrillZoom(1.1)}
                className="p-2.5 rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
                title="Zoom In"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  <line x1="11" y1="8" x2="11" y2="14"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <div className="flex items-center bg-gray-50 rounded-md border overflow-hidden">
                <span className="text-xs font-medium text-gray-500 px-2">
                  Counts:
                </span>

                <input
                  type="number"
                  disabled={drill?.selectedPageIndex === 0}
                  value={counts}
                  onChange={(e) => {
                    setCounts(Number(e.target.value) || 8);
                  }}
                  onBlur={(e) => {
                    const page = drill?.pages[drill?.selectedPageIndex ?? 0];
                    if (page) {
                      page.counts = Number(e.target.value) || 8;
                    }
                  }}
                  className="w-12 border-0 bg-transparent py-1 px-1 text-sm focus:ring-0"
                />
              </div>

              <div className="flex items-center bg-gray-50 rounded-md border overflow-hidden">
                <span className="text-xs font-medium text-gray-500 px-2">
                  Tempo:
                </span>

                <input
                  type="number"
                  value={tempo}
                  onChange={(e) => {
                    setTempo(Number(e.target.value) || 120);
                  }}
                  onBlur={(e) => {
                    const page = drill?.pages[drill?.selectedPageIndex ?? 0];
                    if (page) {
                      page.tempo = Number(e.target.value) ?? 120;
                    }
                  }}
                  min="1"
                  max="300"
                  className="w-14 border-0 bg-transparent py-1 px-1 text-sm focus:ring-0"
                />
              </div>

              <div className="flex items-center bg-gray-50 rounded-md border px-2 py-1">
                <span className="text-xs font-medium text-gray-500">Page:</span>
                <span className="text-xs font-medium ml-1">
                  {(drill?.selectedPageIndex ?? 0) + 1} /{" "}
                  {drill?.pages?.length || 0}
                </span>
              </div>

              <div className="flex items-center bg-gray-50 rounded-md border px-2 py-1">
                <span className="text-xs font-medium text-gray-500">
                  Count:
                </span>
                <span className="text-xs font-medium ml-1">
                  {Math.floor(playbackState.currentCount + 1)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
